interface Property {
  id: number;
  space: {
    road_address: string;
    building_type: string;
    room_count: number;
    bathroom_count: number;
    exclusive_area: number;
    floor: string;
  } | null;
  rent: {
    weekly_rent: number;
    deposit: number;
    weekly_maintenance_fee: number;
  } | null;
  descriptions: {
    room_name: string;
    room_introduction: string;
    transportation_info: string;
  } | null;
  photos: Array<{
    photo_url: string;
    display_order: number;
  }>;
}

interface ExposureCardProps {
  property: Property;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  formatPrice: (price: number | undefined) => string;
  convertImageUrl: (url: string) => string;
}

const ExposureCard = ({
  property,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
  formatPrice,
  convertImageUrl,
}: ExposureCardProps) => {
  const photo = property.photos?.[0];
  const space = property.space;
  const rent = property.rent;
  const desc = property.descriptions;

  // 건물유형 · 교통정보
  const typeAndTransport = [
    space?.building_type,
    desc?.transportation_info,
  ].filter(Boolean).join(' · ');

  // 방/화장실 정보
  const roomSpec = space
    ? `방${space.room_count} 화장실${space.bathroom_count}`
    : '';

  // 면적 · 층수 · 방 스펙
  const areaAndSpec = [
    space?.exclusive_area ? `전용 ${space.exclusive_area}m²` : null,
    space?.floor || null,
    roomSpec || null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      style={{
        display: 'flex',
        gap: '12px',
        padding: '12px',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.15s, box-shadow 0.15s',
        backgroundColor: isHovered ? '#f8f8f8' : 'transparent',
        boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      {/* 썸네일 */}
      <div style={{
        width: '140px',
        minWidth: '140px',
        height: '110px',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#f0f0f0',
      }}>
        {photo ? (
          <img
            src={convertImageUrl(photo.photo_url)}
            alt={desc?.room_name || '매물 사진'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ccc',
            fontSize: '12px',
          }}>
            사진 없음
          </div>
        )}
      </div>

      {/* 텍스트 정보 */}
      <div style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '3px',
      }}>
        {/* 건물유형 · 교통 */}
        {typeAndTransport && (
          <div style={{
            fontSize: '13px',
            color: '#888',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {typeAndTransport}
          </div>
        )}

        {/* 주간 임대료 */}
        <div style={{
          fontSize: '17px',
          fontWeight: 700,
          color: '#222',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {formatPrice(rent?.weekly_rent)}원
          <span style={{ fontSize: '13px', fontWeight: 400, color: '#FF8C00' }}> / 1주</span>
        </div>

        {/* 보증금 · 관리비 */}
        <div style={{
          fontSize: '13px',
          color: '#888',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          보증금 {formatPrice(rent?.deposit)}원 · 관리비 {formatPrice(rent?.weekly_maintenance_fee)}원
        </div>

        {/* 면적 · 층수 · 방 스펙 */}
        {areaAndSpec && (
          <div style={{
            fontSize: '13px',
            color: '#888',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {areaAndSpec}
          </div>
        )}

        {/* 방 소개 */}
        {desc?.room_introduction && (
          <div style={{
            fontSize: '12px',
            color: '#aaa',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: '2px',
          }}>
            {desc.room_introduction}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExposureCard;
